import { useEffect, useState } from "react";
import { Briefcase, Users, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = [
  {
    key: "Luxury_Sedan",
    label: "Luxury Sedan",
    pax: 3,
    luggage: 2,
    tagline: "Discreet executive travel",
  },
  {
    key: "Premium_SUV",
    label: "Premium SUV",
    pax: 6,
    luggage: 6,
    tagline: "Full-size luxury",
  },
  {
    key: "Executive_Sprinter",
    label: "Executive Sprinter",
    pax: 14,
    luggage: 14,
    tagline: "First-class lounge",
  },
  {
    key: "Motor_Coach",
    label: "Motor Coach",
    pax: 56,
    luggage: 56,
    tagline: "Large group transport",
  },
];

/**
 * Fetches a quote for each vehicle category and renders a card list.
 * Calls onSelect(categoryKey, quote) when user picks one.
 */
export default function FleetSelector({ quoteInputs, selected, onSelect }) {
  const [quotes, setQuotes] = useState({}); // {category: {quote, flight}}
  const [loadingCats, setLoadingCats] = useState({});
  const [errors, setErrors] = useState({});

  // Validity check
  const baseReady =
    quoteInputs?.service_type &&
    quoteInputs?.pickup_address &&
    quoteInputs?.pickup_datetime;
  const needsDropoff = quoteInputs?.service_type !== "hourly";
  const ready =
    baseReady &&
    (!needsDropoff || quoteInputs?.dropoff_address) &&
    (quoteInputs?.service_type !== "hourly" || quoteInputs?.duration_hours);

  useEffect(() => {
    if (!ready) {
      setQuotes({});
      setErrors({});
      return;
    }
    let alive = true;
    setQuotes({});
    setErrors({});
    setLoadingCats(Object.fromEntries(CATEGORIES.map((c) => [c.key, true])));

    CATEGORIES.forEach((cat) => {
      const body = { ...quoteInputs, vehicle_category: cat.key };
      api
        .post("/quotes", body)
        .then(({ data }) => {
          if (!alive) return;
          setQuotes((q) => ({ ...q, [cat.key]: data }));
        })
        .catch((err) => {
          if (!alive) return;
          setErrors((e) => ({
            ...e,
            [cat.key]:
              err?.response?.data?.detail || "Unable to price this option",
          }));
        })
        .finally(() => {
          if (!alive) return;
          setLoadingCats((l) => ({ ...l, [cat.key]: false }));
        });
    });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(quoteInputs), ready]);

  if (!ready) {
    return (
      <div
        data-testid="fleet-selector-disabled"
        className="text-center text-xs uppercase tracking-[0.3em] text-[#8E9AA6] py-12 border border-dashed border-white/10"
      >
        Complete trip details to see live pricing
      </div>
    );
  }

  return (
    <div
      data-testid="fleet-selector"
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
    >
      {CATEGORIES.map((cat) => {
        const data = quotes[cat.key];
        const isLoading = loadingCats[cat.key];
        const err = errors[cat.key];
        const isSelected = selected === cat.key;
        const total = data?.quote?.total_price;
        return (
          <button
            key={cat.key}
            type="button"
            onClick={() => data && onSelect(cat.key, data)}
            disabled={!data || !!err}
            data-testid={`fleet-option-${cat.key.toLowerCase()}`}
            className={`group relative text-left p-6 border transition-all duration-300 ${
              isSelected
                ? "border-white bg-white/[0.06]"
                : "border-white/10 bg-[#0B0B0C]/40 hover:border-white/30 hover:bg-white/[0.03]"
            } ${!data && !err ? "opacity-70" : ""} disabled:cursor-not-allowed`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-2">
                  {cat.tagline}
                </div>
                <div className="font-serif text-2xl font-semibold text-white">
                  {cat.label}
                </div>
              </div>
              <div
                className={`text-[10px] uppercase tracking-[0.3em] px-2 py-1 border ${
                  isSelected ? "border-white text-white" : "border-white/20 text-[#8E9AA6]"
                }`}
              >
                {isSelected ? "Selected" : "Select"}
              </div>
            </div>

            <div className="flex gap-6 text-xs text-[#8E9AA6] mb-5">
              <span className="flex items-center gap-1.5">
                <Users size={12} strokeWidth={1.5} /> Up to {cat.pax}
              </span>
              <span className="flex items-center gap-1.5">
                <Briefcase size={12} strokeWidth={1.5} /> Up to {cat.luggage}
              </span>
            </div>

            <div className="border-t border-white/10 pt-4 flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">
                Quoted total
              </span>
              {isLoading ? (
                <span className="text-sm text-[#8E9AA6] tracking-wider">…</span>
              ) : err ? (
                <span className="text-xs text-red-400">{err}</span>
              ) : (
                <span
                  data-testid={`fleet-option-${cat.key.toLowerCase()}-price`}
                  className="font-serif text-3xl text-white font-semibold tracking-tight"
                >
                  ${total?.toFixed(2)}
                </span>
              )}
            </div>

            {isSelected && (
              <div className="absolute -bottom-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
            )}
          </button>
        );
      })}
    </div>
  );
}
